import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, CreditCard, ArrowUpCircle, Gift, X, KeyRound, Trash2, Sparkles, Clock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIER_LABEL = { free: "Free (no membership)", trial: "24-Hour Free Pass", student: "Student Annual", pro: "Pro Annual", lifetime: "Lifetime" };

function TrialTimeLeft({ endsAt }) {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (ms <= 0) return <span className="text-red-600 font-medium">Trial ended</span>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return <span className="font-semibold">{h}h {m}m left</span>;
}

export default function Account() {
  const { user, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  if (!user) return null;

  const cancelRenewal = async () => {
    if (!window.confirm(user.intro_plan
      ? "Cancel? You keep full access until your intro month ends and you won't be charged the annual fee."
      : "Cancel your annual renewal? Your access continues until your renewal date.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/account/cancel-renewal");
      toast.success(data.message);
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const startCancel = async () => {
    try {
      const { data } = await api.get("/account/retention-offer");
      if (data.eligible) { setOffer(data); return; }
    } catch (e) { /* fall through to normal cancel */ }
    cancelRenewal();
  };

  const acceptOffer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/retention-offer/accept");
      toast.success(data.message);
      setOffer(null);
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const declineOffer = () => {
    setOffer(null);
    cancelRenewal();
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/account/billing-portal");
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwNew !== pwNew2) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password changed");
      setPwOpen(false); setPwCurrent(""); setPwNew(""); setPwNew2("");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const submitDelete = async (e) => {
    e.preventDefault();
    if (delConfirm.trim().toUpperCase() !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
    setBusy(true);
    try {
      await api.post("/account/delete", { password: delPw, confirm: delConfirm });
      toast.success("Your account has been deleted");
      setUser && setUser(false);
      navigate("/");
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const canUpgrade = user.membership === "student" || user.membership === "pro" || user.membership === "free" || user.membership === "trial";
  const isTrial = user.membership === "trial";
  const isFree = user.membership === "free";

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-10 px-4">
      <div className="max-w-lg mx-auto space-y-5">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">My Account</h1>

        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="account-membership-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Membership</p>
          <p className="font-display text-xl font-bold mt-1.5" data-testid="account-membership-label">{user.intro_plan ? (user.membership_label || "Intro Month (Pro Access)") : (TIER_LABEL[user.membership] || user.membership)}</p>
          <p className="text-sm text-slate-500 mt-1">{user.email}</p>
          {isTrial && user.trial_ends_at && (
            <p className="text-sm text-emerald-800 bg-emerald-50 rounded-lg px-3 py-2 mt-2 font-medium flex items-center gap-2" data-testid="account-trial-status">
              <Clock size={15} className="text-emerald-700" /> <TrialTimeLeft endsAt={user.trial_ends_at} />
              <span className="text-slate-600 font-normal">— trial ends {new Date(user.trial_ends_at).toLocaleString()}</span>
            </p>
          )}
          {isFree && !user.trial_used && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5"><Sparkles size={15} /> You haven't tried the 24-hour free pass yet</p>
              <button data-testid="account-start-trial-btn" onClick={() => navigate("/start-trial")}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800 hover:text-emerald-900 underline">Start free 24-hour pass — no card needed</button>
            </div>
          )}
          {isFree && user.trial_used && (
            <p className="text-sm text-slate-600 mt-2" data-testid="account-trial-used">Your free trial has been used. Choose a plan below for full access.</p>
          )}
          {user.membership === "lifetime" && <p className="text-sm text-emerald-700 font-medium mt-2">Pay once. Use it for life. No renewal date.</p>}
          {user.first_payment_date && user.first_payment_date >= new Date().toISOString().slice(0, 10) && user.membership !== "lifetime" && (
            <p className="text-sm text-emerald-800 bg-emerald-50 rounded-lg px-3 py-2 mt-2 font-medium" data-testid="account-trial-note">
              Free trial — first payment on <strong>{user.first_payment_date}</strong>.{!user.renewal_cancelled && " Cancel before then and pay nothing."}
            </p>
          )}
          {user.intro_plan && user.renewal_date && !user.renewal_cancelled && (
            <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2 mt-2 font-medium" data-testid="account-intro-note">
              Intro offer — full Pro access. Converts to Pro Annual (£47/year) on <strong>{user.renewal_date}</strong> unless you cancel before then.
            </p>
          )}
          {user.renewal_date && user.membership !== "lifetime" && (
            <p className="text-sm text-slate-600 mt-2" data-testid="account-renewal-date">
              {user.renewal_cancelled ? "Access until" : "Renews on"}: <span className="font-semibold">{user.renewal_date}</span>
              {user.renewal_cancelled && <span className="text-red-500 font-medium"> (renewal cancelled)</span>}
            </p>
          )}
        </div>

        {canUpgrade && (
          <div className="bg-[#0B0F0D] rounded-2xl p-6 text-white">
            <p className="font-display font-bold flex items-center gap-2"><ArrowUpCircle size={18} className="text-[#D4AF37]" /> Upgrade your membership</p>
            <p className="text-sm text-gray-400 mt-1.5">
              {user.membership === "free" && "Choose a plan to unlock the full dictionary."}
              {user.membership === "trial" && "Upgrade any time to keep access after your 24-hour pass ends."}
              {user.membership === "student" && "Upgrade to Pro for the full business library, or go Lifetime and never renew again."}
              {user.membership === "pro" && "Go Lifetime — pay once and never renew again."}
            </p>
            <button data-testid="account-upgrade-btn" onClick={() => navigate("/pricing")}
              className="mt-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold px-5 py-2.5 rounded-xl text-sm hover:brightness-105 transition-all">View plans</button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
          {(user.membership !== "free") && (
            <a data-testid="account-cheatsheet-btn" href={`${process.env.REACT_APP_BACKEND_URL}/api/cheatsheet`}
              className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 hover:text-emerald-700 transition-colors">
              <Download size={17} /> Download Cheat Sheet
            </a>
          )}
          <button data-testid="account-refer-btn" onClick={() => navigate("/refer-a-friend")}
            className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 hover:text-emerald-700 transition-colors">
            <Gift size={17} /> Refer a Friend — earn free ebooks
          </button>
          <button data-testid="account-change-password-btn" onClick={() => setPwOpen(true)}
            className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 hover:text-emerald-700 transition-colors">
            <KeyRound size={17} /> Change password
          </button>
          {user.membership !== "free" && user.membership !== "lifetime" && (
            <button data-testid="account-billing-btn" onClick={openPortal}
              className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 hover:text-emerald-700 transition-colors">
              <CreditCard size={17} /> Update payment method
            </button>
          )}
          {user.membership !== "free" && user.membership !== "lifetime" && !user.renewal_cancelled && (
            <button data-testid="account-cancel-renewal-btn" onClick={startCancel} disabled={busy}
              className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors disabled:opacity-50">
              {user.intro_plan ? "Cancel membership" : "Cancel annual renewal"}
            </button>
          )}
          {user.role !== "admin" && (
            <button data-testid="account-delete-btn" onClick={() => setDelOpen(true)}
              className="flex items-center gap-2.5 text-sm font-medium text-red-500 hover:text-red-700 transition-colors pt-1 border-t border-slate-100 mt-2">
              <Trash2 size={15} /> Delete my account
            </button>
          )}
        </div>
      </div>

      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="change-password-modal">
          <form onSubmit={submitPassword} className="w-full max-w-md bg-white rounded-2xl p-7 relative">
            <button type="button" onClick={() => setPwOpen(false)} aria-label="Close" className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X size={18} /></button>
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><KeyRound size={18} /> Change password</h2>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mt-5">Current password</label>
            <input data-testid="change-password-current" type="password" required value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" autoComplete="current-password" />
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mt-4">New password (min 6)</label>
            <input data-testid="change-password-new" type="password" required minLength={6} value={pwNew} onChange={(e) => setPwNew(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" autoComplete="new-password" />
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mt-4">Confirm new password</label>
            <input data-testid="change-password-confirm" type="password" required value={pwNew2} onChange={(e) => setPwNew2(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" autoComplete="new-password" />
            <button data-testid="change-password-submit" type="submit" disabled={busy}
              className="mt-5 w-full bg-[#0B0F0D] text-[#D4AF37] font-extrabold py-3 rounded-xl hover:bg-[#1a2420] disabled:opacity-50">
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        </div>
      )}

      {delOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="delete-account-modal">
          <form onSubmit={submitDelete} className="w-full max-w-md bg-white rounded-2xl p-7 relative border-t-4 border-red-500">
            <button type="button" onClick={() => setDelOpen(false)} aria-label="Close" className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X size={18} /></button>
            <h2 className="font-display text-xl font-bold flex items-center gap-2 text-red-700"><Trash2 size={18} /> Delete my account</h2>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
              This is permanent. Your account, favourites, recent history and training progress will be deleted. If you're on a paid plan, any remaining access is lost and no refund is issued (see our Subscription Terms).
            </p>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mt-5">Password (to confirm it's really you)</label>
            <input data-testid="delete-account-password" type="password" required value={delPw} onChange={(e) => setDelPw(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none" autoComplete="current-password" />
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mt-4">Type DELETE to confirm</label>
            <input data-testid="delete-account-confirm" type="text" required value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
              className="mt-1.5 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-red-500 focus:ring-2 focus:ring-red-100 outline-none font-mono uppercase" />
            <button data-testid="delete-account-submit" type="submit" disabled={busy || delConfirm.trim().toUpperCase() !== "DELETE"}
              className="mt-5 w-full bg-red-600 text-white font-extrabold py-3 rounded-xl hover:bg-red-700 disabled:opacity-50">
              {busy ? "Deleting…" : "Permanently delete my account"}
            </button>
          </form>
        </div>
      )}

      {offer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="retention-offer-modal">
          <div className="w-full max-w-md bg-[#0B0F0D] border border-[#D4AF37]/40 rounded-2xl p-7 text-white relative">
            <button data-testid="retention-close-btn" onClick={() => setOffer(null)} aria-label="Close"
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
              <Gift size={22} className="text-slate-950" />
            </div>
            <h2 className="font-display text-2xl font-bold mt-4">Wait — before you go…</h2>
            <p className="text-sm text-gray-300 mt-2">
              Stay with us and get <span className="text-[#D4AF37] font-bold">{offer.percent}% off</span> your first payment.
              Your exclusive discount code:
            </p>
            <div className="mt-4 bg-white/5 border border-dashed border-[#D4AF37]/60 rounded-xl px-4 py-3 text-center">
              <span data-testid="retention-offer-code" className="font-mono text-lg font-bold tracking-widest text-[#D4AF37] uppercase">{offer.code}</span>
              <p className="text-xs text-gray-400 mt-1">{offer.percent}% off — applied instantly, nothing to type</p>
            </div>
            <button data-testid="retention-accept-btn" onClick={acceptOffer} disabled={busy}
              className="mt-5 w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-bold py-3 rounded-xl text-sm hover:brightness-105 transition-all disabled:opacity-50">
              Apply {offer.percent}% discount & keep my membership
            </button>
            <button data-testid="retention-decline-btn" onClick={declineOffer} disabled={busy}
              className="mt-3 w-full text-sm font-medium text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50">
              No thanks, cancel anyway
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
